import { I18n } from 'aws-amplify';
import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { getTicketListToArchivedAfterDeleteUserFromCorporation } from '@/service/ticketService';
import {
    getUserById,
    updateUserResource,
    getUserListInCorporationInUserManagement,
    handleRestoreUser
} from '@/service/userService';
import {
    getProjectBaseByCorporationId,
    getProjectListInCorporationByUserId,
} from '@/service/projectService';
import { cloneDeep } from 'lodash';
import { prepareTicketListWithoutNoticeGroup } from '@/component/project/handler/CustomerListInProjectHandler';
import _ from 'lodash';

/**
 * Init add user
 * @param {*} corporationDetail
 * @returns user
 */
export const initAddUser = corporationDetail => {
    let user = { userType: '' };
    switch (corporationDetail.contractType) {
        // premium contract
        case Constant.CORPORATION_CONTRACT_TYPE.PREMIUM:
            user.userType = Constant.USER_TYPE.GENERAL;
            user.isGuest = Constant.GUEST_USER_FLAG.FALSE;
            break;
        // guest contract
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST:
        case Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK:
            user.userType = Constant.USER_TYPE.GENERAL;
            user.isGuest = Constant.GUEST_USER_FLAG.TRUE;
            break;
        default:
    }
    return user;
};

/**
 * Get project list of user
 * @param {*} args
 * @returns projectList
 */
export const getProjectListOfUser = async args => {
    try {
        let projectList = await getProjectListInCorporationByUserId(
            args.API,
            args.corporationId,
            args.userId
        );
        args.setProjectList(projectList);
    } catch (err) {
        console.log('getProjectListOfUser error: ', err);
        return [];
    }
};

export const isExpiredUser = expiredAtByMillisecond => {
    const todayByMillisecond = new Date(Date.now()).getTime();
    return todayByMillisecond > expiredAtByMillisecond ? true : false;
};

/**
 * Get user list
 * @param {*} args
 * @returns userList
 */
export const getUserList = args => {
    args.setIsLoading(true);
    getUserListInCorporationInUserManagement(args.API, args.corporationId)
        .then(async userList => {
            const userListSorted = Utils.sortByCreatedAtDesc(userList);
            args.setUserList(userListSorted);
        })
        .catch(err => {
            args.enqueueSnackbar(I18n.get(err.errors[0].message), {
                variant: 'error',
                autoHideDuration: 3000
            });
        })
        .finally(() => {
            args.setIsLoading(false);
        });
};

/**
 * isDisableBtnDeleteOrgUser
 * is disable btn delete org user
 * @param {*} argsAll 
 * @returns 
 */
 export const isDisableBtnDeleteOrgUser = (argsAll, user) => {
    if (!argsAll.userDetail || Utils.isEmptyObject(argsAll.userDetail) || !Utils.isAdminUser(argsAll.userDetail)) {
        return true;
    }

    if (!user || Utils.isEmptyObject(user) || user.active === Constant.ACTIVE_FLAG.DELETED) {
        return true;
    }

    return false;
}

/**
 * isDisableBtnEditUser
 * @param {*} argsAll 
 * @param {*} user 
 * @returns 
 */
export const isDisableBtnEditUser = (argsAll, user) => {
    if (!argsAll.userDetail || Utils.isEmptyObject(argsAll.userDetail) || !Utils.isAdminUser(argsAll.userDetail)) {
        return true;
    }

    if (!user || Utils.isEmptyObject(user) || user.active === Constant.ACTIVE_FLAG.DELETED) {
        return true;
    }

    return false;
}

/**
 * deleteOrgUser
 * @param {*} argsAll 
 * @param {*} user 
 * @returns 
 */
export const deleteOrgUser = async (argsAll, user) => {
    if (!user || Utils.isEmptyObject(user)) {
        return;
    }

    if(Utils.getUserStatus(user) === Constant.STATUS_OF_USER.DELETED) {
        return;
    }
    
    argsAll.setIsOpenDialogConfirmDelete({
        isEnable: true,
        userId: user.id
    })
}


/**
 * handleDeleteOrgUser
 * @param {*} argsAll 
 * @param {*} userId 
 * @returns 
 */
export const handleDeleteOrgUser = async (argsAll, userId) => {
    if (!userId) return;
    argsAll.setIsLoading(true);

    // delete user org
    const userDeleted = {
        deletedUserId: userId,
        corporationId: argsAll.corporationDetail.id,
        projectBaseId: argsAll.projectBase.id,
        actionFlag: Constant.USERMANAGEMENT_ACTION_FLAG.DELETE_USER_IN_ORG,
    };
    let deleteResult = await updateUserResource(argsAll.API, userDeleted);
    if (!deleteResult || !deleteResult.status) {
        argsAll.setIsLoading(false);
        if (deleteResult?.errorMessage) {
            argsAll.setIsOpenModalRejectDeleteUser(true);
            argsAll.setErrorDeleteUserMsgCode(deleteResult?.errorMessage);
            return;
        }

        if (deleteResult?.error) {
            argsAll.setIsOpenModalRejectDeleteUser(true);
            argsAll.setErrorDeleteUserMsgCode(deleteResult?.error);
        } else {
            argsAll.setIsOpenModalReloadPage(true);
        }
        
        return;
    }
    let currentUserIndexInUserList = Utils.findIndexInArrayById(userId, argsAll.userList);
    let currentUserList = cloneDeep(argsAll.userList);
    currentUserList[currentUserIndexInUserList].active = Constant.ACTIVE_FLAG.DELETED;
    argsAll.setUserList(currentUserList);

    argsAll.setIsLoading(false);
}


/**
 * getTicketListToArchivedAfterDeleteUser
 * @param {*} argsAll 
 * @param {*} userId 
 */
export const getTicketListToArchivedAfterDeleteUser = async (argsAll, userId) => {
    let projectBaseId = argsAll.projectBase.id;
    let allTicketToArchived = await getTicketListToArchivedAfterDeleteUserFromCorporation(argsAll.API, argsAll.corporationId, userId, projectBaseId);
    let ticketListToArchivedResponse = await prepareTicketListWithoutNoticeGroup(argsAll.API, allTicketToArchived);
    return ticketListToArchivedResponse;
}

/**
 * Get project base by corporation
 * @param {*} args
 * @returns projectBase of corporation
 */
export const getProjectBase = args => {
    // args.setIsLoading(true);
    getProjectBaseByCorporationId(args.API, args.corporationId)
        .then(projectBase => {
            args.setProjectBase(projectBase);
        })
        .catch(err => {
            args.enqueueSnackbar(I18n.get(err.errors[0].message), {
                variant: 'error',
                autoHideDuration: 3000
            });
        })
        .finally(() => {
            // args.setIsLoading(false);
        });
};

export const validatedEmail = (email, userAddList, userId) => {
    let errors;
    // validate email
    if (Utils.isEmptyString(email.trim())) {
        errors = I18n.get('COMMSG014');
    } else if (!Utils.validateEmail(email.trim())) {
        errors = I18n.get('COMM01MSG07');
    } else if (email.trim().length > Constant.EMAIL_MAX_LENGTH) {
        errors = I18n.get('COMMSG027').replace('{{maxLength}}', Constant.EMAIL_MAX_LENGTH);
    } else if (email.trim().length < 4) {
        errors = I18n.get('COMMSG026').replace('{{minLength}}', 4);
    } else {
        let emails = userAddList.filter(x => x.mailAddress.trim().toLowerCase() === email.trim().toLowerCase() && x.id !== userId);
        let isExistEmail = emails && emails.length > 0 ? true : false;
        if (isExistEmail) {
            errors = I18n.get('USER09MSG007');
        } else {
            errors = '';
        }
    }

    return errors;
};

export const validatedNameUser = name => {
    let errors;
    if (Utils.isEmptyString(name.trim())) {
        errors = I18n.get('COMMSG014');
    } else if (name.trim().length > 200) {
        errors = I18n.get('COMMSG027').replace('{{maxLength}}', '200');
    } else {
        errors = '';
    }

    return errors;
};

/**
 * Handle text field change
 * @param {*} event
 * @param {*} userAdd
 * @param {*} args
 */
export const handleTextFieldChange = (event, userAdd, args) => {
    userAdd = userAdd === undefined || null ? {} : userAdd;
    let inputValue = event.target.value;
    switch (event.target.id) {
        case 'mailAddress':
            userAdd.mailAddress = inputValue;
            break;
        case 'firstName':
            userAdd.firstName = inputValue;
            break;
        case 'lastName':
            userAdd.lastName = inputValue;
            break;
        default:
            return;
    }
    let userAddClone = { ...userAdd };
    args.setUserAdd(userAddClone);
};

export const findMailDuplicate = (userAddList) => {
    let valueArr = userAddList.map(item => item.mailAddress.trim().toLowerCase()).filter(item => !!item);
    const mailDuplicate = valueArr.filter((item, idx) => valueArr.indexOf(item) !== idx);
    return mailDuplicate;
}

export const handleTextFieldAddChange = (event, userId, userAddList, setUserAddList) => {
    let currentAddList = cloneDeep(userAddList);
    // let currentUserList = cloneDeep(args.userList);
    // let errorAddList = args.errorAddList;
    let inputValue = event.target.value;
    // let errorsRow = errorAddList[`${userId}`] ? errorAddList[`${userId}`] : {};
    const index = Utils.findIndexInArrayById(userId, currentAddList);
    let errors = '';
    switch (event.target.id) {
        case `mailAddress-${userId}`:
            // errors = validatedEmail(inputValue, currentAddList, userId);
            // if (!Utils.isEmptyString(errors)) {
            //     errorsRow.email = errors;
            //     errorAddList[`${userId}`] = errorsRow;
            //     currentAddList[index].mailAddress = inputValue;
            //     args.setErrorAddList(errorAddList);
            // } else {
                // let currentEmail = inputValue.trim().toLowerCase();
                // let isExistEmail = currentUserList.find(existUser => existUser.mail === currentEmail);
                // if (isExistEmail && !Utils.isEmptyObject(isExistEmail)) {
                //     errorsRow.email = I18n.get('USER09MSG007');
                //     errorAddList[`${userId}`] = errorsRow;
                //     currentAddList[index].mailAddress = inputValue;
                //     args.setErrorAddList(errorAddList);
                // } else {
                //     delete errorsRow.email;
                //     errorAddList[`${userId}`] = errorsRow;
                    currentAddList[index].mailAddress = inputValue;
            //         args.setErrorAddList(errorAddList);
            //     }
            // }
            break;
        // case `firstName-${userId}`:
        //     errors = validatedNameUser(inputValue);

        //     if (!Utils.isEmptyString(errors)) {
        //         errorsRow.firstName = errors;
        //         errorAddList[`${userId}`] = errorsRow;
        //         args.setErrorAddList(errorAddList);
        //     } else {
        //         delete errorsRow.firstName;
        //         errorAddList[`${userId}`] = errorsRow;
        //         args.setErrorAddList(errorAddList);
        //     }
        //     currentAddList[index].firstName = inputValue;
        //     break;
        // case `lastName-${userId}`:
        //     errors = validatedNameUser(inputValue);

        //     if (!Utils.isEmptyString(errors)) {
        //         errorsRow.lastName = errors;
        //         errorAddList[`${userId}`] = errorsRow;
        //         args.setErrorAddList(errorAddList);
        //     } else {
        //         delete errorsRow.lastName;
        //         errorAddList[`${userId}`] = errorsRow;
        //         args.setErrorAddList(errorAddList);
        //     }
        //     currentAddList[index].lastName = inputValue;
        //     break;
        default:
            return;
    }
    setUserAddList(currentAddList);
    // let mailDuplicate = findMailDuplicate(currentAddList);
    // currentAddList.forEach(item => {
    //     if (Utils.isEmptyObject(errorsRow) && !mailDuplicate.includes(item.mailAddress.trim().toLowerCase()) && errorAddList[`${item.id}`]?.email === I18n.get('USER09MSG007')) {
    //         delete errorAddList[`${item.id}`]?.email;
    //         args.setErrorAddList(errorAddList);
    //     }
    // });
};

export const handleTextFieldOnClick = event => {
    event.target.focus();
};

/**
 * Handle select change admin of add add user
 * @param {*} event
 * @param {*} userAdd
 * @param {*} args
 */
export const handleSelectChangeAdminOfAddUser = (event, userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    let index = Utils.findIndexInArrayById(userId, currentAddList);
    let admin = [Constant.PRIVILEGE_USER.ADMIN_OWNER, Constant.PRIVILEGE_USER.ADMIN].includes(
        currentAddList[index].privilege
    )
        ? Constant.ADMIN.OFF
        : Constant.ADMIN.ON;

    currentAddList[index].isChangePermission =
        currentAddList[index].isChangePermission === undefined
            ? true
            : !currentAddList[index].isChangePermission;

    currentAddList[index].privilege =
        Constant.ADMIN.ON === admin ? Constant.PRIVILEGE_USER.ADMIN : '';
    args.setUserAddList(currentAddList);
};

/**
 * Hadle select change userType of add user
 * @param {*} event
 * @param {*} userAdd
 * @param {*} args
 */
export const handleSelectChangeUserTypeOfAddUser = (event, userAdd, args) => {
    userAdd = userAdd === undefined || null ? {} : userAdd;
    let userAddClone = { ...userAdd };
    let valueSelect = event.target.value;
    userAddClone.userType = valueSelect;
    userAddClone.admin = Constant.ADMIN.OFF;
    args.setUserAdd(userAddClone);
};

/**
 * Handle delete multiple select user
 * @param {*} index
 * @param {*} userList
 * @param {*} userDeleteList
 * @param {*} userDetail
 * @param {*} args
 */
export const onDeleteSelect = (index, userList, userDeleteList, userDetail, args) => {
    let userSelected = userList[index];
    if (userDetail.id === userSelected.id) {
        return;
    }

    let userListClone = [...userList];
    userListClone[index].isDelete =
        userListClone[index].isDelete === undefined ? true : !userListClone[index].isDelete;
    args.setUserList([...userListClone]);

    // update list delete
    if (userListClone[index].isDelete) {
        userDeleteList.push(userListClone[index]);
    } else {
        let userId = userListClone[index].userId;
        userDeleteList = userDeleteList.filter(item => item.userId !== userId);
    }
    let newDeleteList = [...userDeleteList];
    args.setUserDeleteList(newDeleteList);
};

export const onDeleteAddUser = (userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    let errorAddList = args.errorAddList;
    currentAddList = currentAddList.filter((e, i) => e.id !== userId);
    
    let mailDuplicate = findMailDuplicate(currentAddList);
    currentAddList.forEach(item => {
        if (!mailDuplicate.includes(item.mailAddress.trim().toLowerCase()) && errorAddList[`${item.id}`]?.email === I18n.get('USER09MSG007')) {
            delete errorAddList[`${item.id}`]?.email;
            args.setErrorAddList(errorAddList);
        }
    });

    args.setUserAddList(currentAddList);
    args.setOpenConfirmModal({ isOpen: false, userId: '' });
};

export const checkIsUpdateUser = argsAll => {
    let hasChange = false;
    if (argsAll.editUserList.length && argsAll.userList.length) {
        argsAll.editUserList.forEach((editUser) => {
            if (!editUser.firstName || !editUser.lastName) {
                return false;
            }
            let userInList = argsAll.userList.find(item => item.id === editUser.id);
            
            if (userInList && !Utils.isEmptyObject(userInList)) {
                if (Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userInList.firstName) !== editUser.firstName || 
                    Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userInList.lastName) !== editUser.lastName) {
                    hasChange = true;
                }
            }
        })
    }
    
    return (
        !Utils.isEmptyArray(argsAll.userChangePermissionList) ||
        !Utils.isEmptyArray(argsAll.userChangeTypeList) ||
        !Utils.isEmptyArray(argsAll.userDeleteList) ||
        hasChange
    );
};

export const checkDisableBtnSubmit = argAll => {
    if (Utils.isEmptyArray(argAll.userAddList)) {
        if (checkIsUpdateUser(argAll)) {
            argAll.setIsDisableBtnSubmit(false);
        } else {
            argAll.setIsDisableBtnSubmit(true);
        }
        return;
    }
    if (Utils.isEmptyObject(validateAddUser(argAll))) {
        argAll.setIsDisableBtnSubmit(false);
    } else {
        argAll.setIsDisableBtnSubmit(true);
    }
};

export const onEditSelect = async (index, argsAll, user) => {
    if(Utils.getUserStatus(user) === Constant.STATUS_OF_USER.DELETED){
        return;
    }
    let editUserList = [...argsAll.editUserList];
    let userListClone = [...argsAll.userList];
    if (typeof userListClone[index].editing !== 'undefined' && userListClone[index].editing === 1) {
        userListClone[index].editing = 0;
        editUserList = editUserList.filter(item => item.id !== userListClone[index].id);
    } else {
        userListClone[index].editing = 1;
        let newEditUser = cloneDeep(userListClone[index]);

        newEditUser.lastName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, newEditUser.lastName);
        newEditUser.firstName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, newEditUser.firstName);

       
        editUserList.push(newEditUser);
    }

   
    // let neweditUserList = [...editUserList];
    argsAll.setEditUserList([...editUserList]);
    argsAll.setUserList([...userListClone]);
};

export const handleEditTextField = (event, userId, args) => {
    let editUserList = cloneDeep(args.editUserList);
    let errorEditList = args.errorEditList;
    let inputValue = event.target.value;
    let errorsRow = errorEditList[`${userId}`] ? errorEditList[`${userId}`] : {};
    let errors = '';
    const index = Utils.findIndexInArrayById(userId, editUserList);

    editUserList[index].hasChange = true;

    switch (event.target.id) {
        case `firstName-${userId}`:
            errors = validatedNameUser(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.firstName = errors;
                errorEditList[`${userId}`] = errorsRow;
            } else {
                delete errorsRow.firstName;
                errorEditList[`${userId}`] = errorsRow;
            }

            args.setErrorEditList(errorEditList);
            editUserList[index].firstName = inputValue;
            break;
        case `lastName-${userId}`:
            errors = validatedNameUser(inputValue);

            if (!Utils.isEmptyString(errors)) {
                errorsRow.lastName = errors;
                errorEditList[`${userId}`] = errorsRow; 
            } else {
                delete errorsRow.lastName;
                errorEditList[`${userId}`] = errorsRow;
            }

            args.setErrorEditList(errorEditList);
            editUserList[index].lastName = inputValue;
            break;
        default:
            return;
    }

    args.setEditUserList(editUserList);
};


/**
 * isDisableChangeAdmin
 * @param {*} userDetail
 * @param {*} user
 * @returns
 */
export const isDisableChangeAdmin = (userDetail, user, argsAll) => {
    let currentUserList = cloneDeep(argsAll.userList);
    let currentAdminList = currentUserList.filter(userInList => Utils.isAdminUser(userInList));

    return (
        !Utils.isAdminUser(userDetail) ||
        !userDetail.type === Constant.USER_TYPE.GENERAL ||
        user.type === Constant.USER_TYPE.INTERNAL ||
        (Utils.isAdminUser(user) && !Utils.isEmptyArray(currentAdminList) && currentAdminList.length === 1 && currentAdminList[0].id === user.id) ||
        // userDetail.id === user.id ||
        (typeof user.editing === "undefined" || !user.editing)
    );
};

/**
 * Handle change admin permission select
 * @param {*} index
 * @param {*} event
 * @param {*} userList
 * @param {*} userChangePermissionList
 * @param {*} args
 */
export const onChangeAdminPremissionSelect = (index, userList, userChangePermissionList, args) => {
    let userListClone = [...userList];

    let admin = [Constant.PRIVILEGE_USER.ADMIN_OWNER, Constant.PRIVILEGE_USER.ADMIN].includes(
        userListClone[index].privilege
    )
        ? Constant.ADMIN.OFF
        : Constant.ADMIN.ON;

    // check permission admin with user type internal
    let userByIndex = userList[index];
    // validate user type with permission admin
    // if user type internal and permission admin is admin then error
    if (Constant.ADMIN.ON === admin && Constant.USER_TYPE.INTERNAL === userByIndex.privilege) {
        args.enqueueSnackbar(I18n.get(Constant.ERROR_CHANGE_USER_ADMIN_WITH_INTERNAL), {
            variant: 'error',
            autoHideDuration: 3000
        });
        return;
    }

    userListClone[index].isChangePermission =
        userListClone[index].isChangePermission === undefined
            ? true
            : !userListClone[index].isChangePermission;

    userListClone[index].privilege =
        Constant.ADMIN.ON === admin ? Constant.PRIVILEGE_USER.ADMIN : '';
    args.setUserList([...userListClone]);

    // update list change permission
    if (userListClone[index].isChangePermission) {
        userChangePermissionList.push(userListClone[index]);
    } else {
        let id = userListClone[index].id;
        userChangePermissionList = userChangePermissionList.filter(item => item.id !== id);
    }
    let newUserChangePermissionList = [...userChangePermissionList];
    args.setUserChangePermissionList(newUserChangePermissionList);
};

/**
 * Handle evnnt change userType select
 * @param {*} index
 * @param {*} event
 * @param {*} userList
 * @param {*} userChangeTypeList
 * @param {*} projectBase
 * @param {*} args
 * @param {*} rpoejctList
 */
export const onChangeUserTypeSelect = (
    index,
    event,
    userList,
    userChangeTypeList,
    projectBase,
    args,
    projectList
) => {
    // check permission admin with user type internal
    let userType = event.target.value;
    let userByIndex = userList[index];
    // validate user type with permission admin
    // if user type internal and permission admin is admin then error
    if (
        Constant.PRIVILEGE_USER.ADMIN === userByIndex.privilege &&
        Constant.USER_TYPE.INTERNAL === userType
    ) {
        args.enqueueSnackbar(I18n.get(Constant.ERROR_CHANGE_USER_ADMIN_WITH_INTERNAL), {
            variant: 'error',
            autoHideDuration: 3000
        });
        return;
    }

    let userListClone = [...userList];
    userListClone[index].isChangeUserType =
        userListClone[index].isChangeUserType === undefined
            ? true
            : !userListClone[index].isChangeUserType;
    userListClone[index].type = userType;
    if ( userType === Constant.USER_TYPE.CUSTOMER || userType === Constant.USER_TYPE.INTERNAL) {
        userListClone[index].privilege = '';
    }

    args.setUserList([...userListClone]);

    // update list change permission
    if (userListClone[index].isChangeUserType) {
        userChangeTypeList.push(userListClone[index]);
    } else {
        let userId = userListClone[index].id;
        userChangeTypeList = userChangeTypeList.filter(item => item.id !== userId);
    }
    let newUserChangeTypeList = [...userChangeTypeList];
    args.setUserChangeTypeList(newUserChangeTypeList);
};

export const onChangeUserAddTypeSelect = (event, userId, args) => {
    let currentAddList = cloneDeep(args.userAddList);
    let inputValue = event.target.value;
    let index = Utils.findIndexInArrayById(userId, currentAddList);

    if (inputValue === Constant.USER_TYPE.INTERNAL) {
        currentAddList[index].isChangePermission = false;
        currentAddList[index].privilege = '';
        currentAddList[index].isDisableChangeSwitch = true;
    } else {
        currentAddList[index].isDisableChangeSwitch = false;
    }
    currentAddList[index].type = inputValue;

    args.setUserAddList(currentAddList);
};

/**
 * Handle btn cancel
 * @param {*} args
 */
export const onCancel = args => {
    let userAddList = cloneDeep(args.userAddList);

    args.setUserChangePermissionList([]);
    args.setUserChangeTypeList([]);
    args.setEditUserList([]);
    cancelEditUserList(args);
    userAddList = userAddList.map((item, index) => {
        return {
            ...item,
            usage: Constant.SYSTEM_USAGE_TYPE.USER,
            affiliationOrganization: args.corporationId,
            mailAddress: '',
            firstName: '',
            lastName: '',
            type: Constant.USER_TYPE.GENERAL,
            isGuest: false,
            privilege: '',
            isDisableDelete: false,
            isChangePermission: false,
            isAddNew: true,
            isDisableChangeSwitch: false
        };
    });
    args.setErrorAddList([]);
    args.setUserAddList(userAddList);
    // getUserList(args);
    args.setOpenCancelDialog(false);
};

export const validateAddUser = args => {
    let userAddList = cloneDeep(args.userAddList);
    if (Utils.isEmptyArray(userAddList)) return;
    let errors = [];
    userAddList.forEach(user => {
        errors[`${user.id}`] = {};
        let errorLastName = validatedNameUser(user.lastName);
        if (!Utils.isEmptyString(errorLastName)) {
            errors[`${user.id}`].lastName = errorLastName;
        } else {
            delete errors[`${user.id}`].lastName;
        }
        let errorFirstName = validatedNameUser(user.firstName);
        if (!Utils.isEmptyString(errorFirstName)) {
            errors[`${user.id}`].firstName = errorFirstName;
        } else {
            delete errors[`${user.id}`].firstName;
        }

        let errorEmail = validatedEmail(user.mailAddress, userAddList, user.id);
        if (!Utils.isEmptyString(errorEmail)) {
            errors[`${user.id}`].email = errorEmail;
        } else {
            delete errors[`${user.id}`].email;
        }

    });
    let errorList = {};
    for (let key in errors) {
        if (!Utils.isEmptyObject(errors[key])) {
            errorList[key] = errors[key];
        }
    }

    return errorList;
};

const getIdFromUserList = (userList) => {
    let userIds = [];
    userList.map((user) => {
        if (user.id) userIds.push(user.id);
    })
    return userIds;
}

const checkUserBeforeDelete = async (args, userChangePermissionList, userChangeTypeList) => {
    const userIds = [...getIdFromUserList(args.editUserList),
         ...getIdFromUserList(userChangePermissionList), ...getIdFromUserList(userChangeTypeList)];
    let hasError = false;
        
    let uniqueUserIds = _.uniq(userIds);
    let userCheckList = await Promise.all(
        uniqueUserIds.map(userId => {
            return getUserById(args.API, userId);
        })
    );

    userCheckList.map((user) => {
        if (user.active === Constant.ACTIVE_FLAG.DELETED) {
            hasError = true;
        }
    })

    return hasError;
}

/**
 * 
 * @param {*} userChangePermissionList 
 * @param {*} userDeleteList 
 * @param {*} userChangeTypeList 
 * @param {*} args 
 * @param {*} projectBase 
 * @param {*} projectList 
 * @returns 
 */
export const onSubmit = async (
    userChangePermissionList,
    userChangeTypeList,
    args
) => {
    try {
        // validate form add new user
        let errors = validateAddUser(args);
        if (errors && !Utils.isEmptyObject(errors)) {
            args.setErrorAddList(errors);
        }
        args.setIsLoading(true);

        let checkUser = await checkUserBeforeDelete(args, userChangePermissionList, userChangeTypeList);
        if (checkUser) {
            args.setIsLoading(false);
            args.setIsOpenModalReloadPage(true);
            return;
        }

        let {userAddList, editUserList} = args;
        let hashUser = {};

        // Add list 
        if(!Utils.isEmptyArray(userAddList)){
            for(let additionalUser of userAddList){
                if(!errors.hasOwnProperty(additionalUser.id) && Utils.isEmpty(hashUser[additionalUser.id])){
                    hashUser[additionalUser.id] = additionalUser;
                }
            }
        }

        // Change user type list
        if(!Utils.isEmptyArray(userChangeTypeList)){
            for(let typeChangedUser of userChangeTypeList){
                if(Utils.isEmpty(hashUser[typeChangedUser.id])){
                    hashUser[typeChangedUser.id] = typeChangedUser;
                }
                else{
                    hashUser[typeChangedUser.id] = Object.assign(hashUser[typeChangedUser.id], {
                        isChangeUserType: typeChangedUser.isChangeUserType,
                        type: typeChangedUser.type
                    })
                }
            }
        }

        // Change admin list
        let isChangeRoleAdminOwner = false;
        if(!Utils.isEmptyArray(userChangePermissionList)){
            for(let adminChangedUser of userChangePermissionList){
                if(Utils.isEmpty(hashUser[adminChangedUser.id])){
                    hashUser[adminChangedUser.id] = adminChangedUser;
                }
                else{
                    hashUser[adminChangedUser.id] = Object.assign(hashUser[adminChangedUser.id], {
                        isChangePermission: adminChangedUser.isChangePermission,
                        privilege: adminChangedUser.privilege
                    })
                }

                if (!adminChangedUser.privilege && args?.corporationDetail?.owner === adminChangedUser?.id) {
                    isChangeRoleAdminOwner = true;
                }
            }
        }

        if (isChangeRoleAdminOwner) {
            args.setIsLoading(false);
            args.setIsOpenDialogEditUser(true);
            args.setDialogEditUserError(I18n.get('MSG-CHANGE-AUTHORITY-REPRESENT-USER'));
            return;
        }

        // Change user info
        if(!Utils.isEmptyArray(editUserList)){
            for(let userChangeInfo of editUserList){
                if(Utils.isEmpty(hashUser[userChangeInfo.id])){
                    hashUser[userChangeInfo.id] = userChangeInfo;
                }
                else{
                    hashUser[userChangeInfo.id] = Object.assign(hashUser[userChangeInfo.id], {
                        hasChange: userChangeInfo.hasChange,
                        firstName: userChangeInfo.firstName,
                        lastName: userChangeInfo.lastName,
                    })
                }
            }
        }

        let uniqueUserArray = [];
        for(let userId in hashUser){
            uniqueUserArray.push(hashUser[userId]);
        }

        uniqueUserArray = uniqueUserArray.map(userInfo => {
            return {
                id: userInfo.id,
                affiliationOrganization: userInfo.affiliationOrganization,
                firstName: userInfo.firstName,
                lastName: userInfo.lastName,
                isAddNew: userInfo.isAddNew,
                isChangePermission: userInfo.isChangePermission,
                isChangeUserType: userInfo.isChangeUserType,
                isChangeInfo: userInfo.hasChange,
                isGuest: [Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(args.corporationDetail?.contractType) ? Constant.GUEST_USER_FLAG.TRUE : Constant.GUEST_USER_FLAG.FALSE,
                mailAddress: userInfo?.mailAddress ? userInfo?.mailAddress?.trim().toLowerCase(): userInfo?.mail.trim().toLowerCase(),
                privilege: userInfo.privilege, 
                type: userInfo.type,
                usage: userInfo.usage,
                admin: userInfo.admin,
            }
        });

        let paramsUpdateUserList = {
            userList: JSON.stringify(uniqueUserArray),
            corporationId: args.corporationDetail.id,
            actionFlag: Constant.USERMANAGEMENT_ACTION_FLAG.CREATE_AND_UPDATE_USER_IN_CORP,
            idOfCurrentUser: args.userDetail.id
        }

        args.setIsLoading(true);
        let result = await updateUserResource(args.API, paramsUpdateUserList);
        if (!result) {
            return;
        }

        if (!result.status) {
            if (result?.error === Constant.ADD_USER_MAX_GENERAL_USER_ERROR) {
                args.setIsOpenDialogEditUser(true);
                args.setDialogEditUserError(I18n.get(result?.error));
            } else if (result?.error) {
                args.enqueueSnackbar(I18n.get(result?.error), {
                    variant: 'error',
                    autoHideDuration: 3000
                });
            }
            args.setIsLoading(false);
            return;
        }

        let msgNoticeSuccess = I18n.get('MSG-CM-UPDATE-DATA-SUCCESS');
        const userSuccessList = Utils.convertJSONDataToObject(result.userSuccessList);
        if(!Utils.isEmptyArray(userAddList) && result.status) {
            let addUserError = {};
            let listAddUserFail = [];

            const listUserAddFail = Utils.convertJSONDataToObject(result.userFailList);
            if (!Utils.isEmptyArray(userSuccessList) && userAddList.length === uniqueUserArray.length) {
                msgNoticeSuccess = I18n.get('USER12MSG006');
            }

            if (!Utils.isEmptyArray(listUserAddFail)) {
                listUserAddFail?.forEach(userFailInfo => {
                    const addUserInfo = args.userAddList.find(addUserInfoItem => {
                        return addUserInfoItem?.mailAddress?.trim()?.toLowerCase() === userFailInfo?.email?.trim()?.toLowerCase();
                    });
                    listAddUserFail.push(addUserInfo);
                    if (!Utils.isEmptyObject(addUserInfo)) {
                        addUserError[addUserInfo.id] = {
                            mailAddress: I18n.get(userFailInfo.errorCode)
                        };
                    }
                });
            }

            args.setUserAddList([...listAddUserFail]);
            args.setErrorAddList({ ...addUserError });
        }
        // check case only add user and all listUserAdd error then don't show msg notify success

        if (
            !(
                Utils.isEmptyArray(editUserList) &&
                Utils.isEmptyArray(userSuccessList) &&
                !Utils.isEmptyArray(userAddList)
            )
        ) {
            args.enqueueSnackbar(msgNoticeSuccess, {
                variant: 'success',
                autoHideDuration: 3000
            });
        }

        args.setEditUserList([]);
        args.setUserChangeTypeList([]);
        args.setUserChangePermissionList([]);
        getUserList(args);
    } catch (error) {
        // Logger.error(error);
        args.setIsLoading(false);
        args.enqueueSnackbar(I18n.get(error.errors[0].message), {
            variant: 'error',
            autoHideDuration: 3000
        });
    }
};


export const isEmptyUserAddList = argsAll => {
    let count = 0;
    let userAddLists = cloneDeep(argsAll.userAddList);
    for (const userItem of userAddLists) {
        let user = { ...userItem };
        if (
            Utils.isEmptyString(user.mailAddress) &&
            Utils.isEmptyString(user.firstName) &&
            Utils.isEmptyString(user.lastName) &&
            Utils.isGeneralUserWithoutGuest(user) &&
            !user.isChangePermission
        ) {
            count += 1;
        }
    }
    return count === userAddLists.length;
};

export const onClickCancelEdit = argsAll => {
    
    argsAll.setUserChangeTypeList([]);
    for (const editUser of argsAll.editUserList) {
        if (editUser?.hasChange) {
            return argsAll.setOpenCancelDialog(true);
        }
    }

    cancelEditUserList(argsAll);

    if (isEmptyUserAddList(argsAll)) {
        argsAll.setUserAddList([]);
        return;
    };
    return argsAll.setOpenCancelDialog(true);
};


export const cancelEditUserList = argsAll => {
    if (argsAll.userList.length && argsAll.editUserList.length) {
        let userListClone = [...argsAll.userList];
        for (const user of userListClone) {
            if (user?.editing) {
                user.editing = 0;
            }
            if (user.isChangeUserType) {
                user.type = user.type === Constant.USER_TYPE.GENERAL ? Constant.USER_TYPE.INTERNAL : Constant.USER_TYPE.GENERAL;
                user.isChangeUserType = false;
            }
            if (user.isChangePermission) {
                user.privilege = user.privilege === Constant.PRIVILEGE_USER.ADMIN ? '' : Constant.PRIVILEGE_USER.ADMIN;
                user.isChangePermission = false;
            }
        }

        argsAll.setUserList(userListClone);
        argsAll.setEditUserList([]);
    }
    argsAll.setErrorEditList({});
}
/**
 * Check disable change userType
 * @param {*} currentUser
 * @param {*} user
 * @returns isDisable
 */
export const isDisableChangeUserType = (currentUser, user) => {
    if (!Utils.isAdminUser(currentUser) || user.isGuest || typeof user.editing === 'undefined' || !user.editing || Utils.isAdminUser(user)) {
        return true;
    }

    if (
        Utils.isGeneralUserWithoutGuest(user) &&
        !Utils.isEmptyObject(user.projectList)
    ) {
        let project = user.projectList.find(
            item => item.projectType !== Constant.PROJECT_TYPE.BASE
        );
        return Utils.isEmpty(project) ? false : true;
    }

    return false;
};

export const handleAddNew = argsAll => {
    // return;
    let newUserData = {
        id: Date.now().toString(),
        usage: Constant.SYSTEM_USAGE_TYPE.USER,
        affiliationOrganization: argsAll.corporationId,
        mailAddress: '',
        firstName: '',
        lastName: '',
        type: Constant.USER_TYPE.GENERAL,
        isGuest: [Constant.CORPORATION_CONTRACT_TYPE.GUEST, Constant.CORPORATION_CONTRACT_TYPE.GUEST_DESK].includes(argsAll?.corporationDetail?.contractType) ? Constant.GUEST_USER_FLAG.TRUE : Constant.GUEST_USER_FLAG.FALSE,
        privilege: '',
        isDisableDelete: false,
        isChangePermission: false,
        isAddNew: true,
        isDisableChangeSwitch: false
    };

    let currentUserList = cloneDeep(argsAll.userAddList);
    argsAll.setUserAddList([newUserData, ...currentUserList]);
};

export const onDeleteOrShowModalUser = (user, argsAll) => {
    if (
        Utils.isEmptyString(user.mailAddress) &&
        Utils.isEmptyString(user.firstName) &&
        Utils.isEmptyString(user.lastName) &&
        Utils.isGeneralUserWithoutGuest(user) &&
        !user.isChangePermission
    ) {
        return onDeleteAddUser(user.id, argsAll);
    }
    return argsAll.setOpenConfirmModal({ isOpen: true, userId: user.id });
};

export const handleSubmitConfirmModal = argsAll => {
    argsAll.setIsLoading(true);
};

export const isDisableBtnRestore = (user) => {
    if(Utils.getUserStatus(user) === Constant.STATUS_OF_USER.DELETED || Utils.getUserStatus(user) === Constant.STATUS_OF_USER.REJECT){
        return false;
    }
    return true;
}

export const onRestoreSelect = async (argsAll, user) => {
    if (!user || Utils.isEmptyObject(user)) {
        return;
    }

    argsAll.setSelectedUser(user);
    argsAll.setIsOpenDialogConfirmRestoreUser(true)
};

export const onSubmitRestoreUser = async (argsAll) => {
    if (!argsAll || !argsAll.selectedUser || Utils.isEmptyObject(argsAll.selectedUser)) return;

    let restoreUserId = argsAll.selectedUser.id;

    let restoreUserRequestDto = {
        userId: restoreUserId,
        type: Constant.RESTORE_USER_TYPE.USER_IN_ORG,
        currentUserId: argsAll.userDetail.id,
        corporationId: argsAll.corporationId,
    }

    argsAll.setIsLoading(true)
    let restoreResult = await handleRestoreUser(argsAll.API, restoreUserRequestDto);
    argsAll.setIsLoading(false);

    if(restoreResult.status){
        let indexRestore = Utils.findIndexInArrayById(argsAll.selectedUser.id, argsAll.userList);
        if(indexRestore >= 0){
            let newUserListRestoreData = cloneDeep(argsAll.userList);
            newUserListRestoreData[indexRestore].active = Constant.ACTIVE_FLAG.INACTIVE;
            newUserListRestoreData[indexRestore].expireAt = `${new Date().getTime() + Constant.TIME_TO_LIVE_REQUEST_CREATE_USER}`;
            newUserListRestoreData[indexRestore].updator = argsAll.userId;
            
            argsAll.setUserList(newUserListRestoreData);
        }

        argsAll.enqueueSnackbar(I18n.get('MSG-CM-UPDATE-DATA-SUCCESS'), {
            variant: 'success',
            autoHideDuration: 3000
        });
    }
    else{
        if (restoreResult?.errorMessage) {
            argsAll.setIsOpenDialogEditUser(true);
            argsAll.setDialogEditUserError(I18n.get(restoreResult?.errorMessage));
            return;
        }

        argsAll.setIsOpenModalReloadPage(true)
    }
}